import React, { useState } from 'react';

// Define types for our component
interface CsvToJsonlProps {}

// CSV to JSONL conversion function
const csvToJsonl = (
  csv: string, 
  options: {
    header: boolean;
    delimiter: string;
    trimValues: boolean;
    skipEmptyLines: boolean;
    detectTypes: boolean;
  }
): string => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return '';
    }
    
    let startRow = 0;
    let headers: string[] = [];
    
    // Process headers if they exist
    if (options.header && lines.length > 0) {
      const headerRow = lines[0];
      headers = parseCSVLine(headerRow, options.delimiter);
      if (options.trimValues) {
        headers = headers.map(header => header.trim());
      }
      startRow = 1;
    }
    
    // Initialize JSONL output
    let jsonlOutput = '';
    
    // Process data rows
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      if (values.length > 0) {
        // Create object for the current row
        const obj: Record<string, any> = {};
        
        if (options.header) {
          // Use headers as keys
          for (let j = 0; j < headers.length; j++) {
            if (j < values.length) {
              let value = options.trimValues ? values[j].trim() : values[j];
              
              // Detect types if enabled
              if (options.detectTypes) {
                value = detectType(value);
              }
              
              obj[headers[j]] = value;
            } else {
              obj[headers[j]] = null; // Set null for missing values
            }
          }
        } else {
          // Use numeric indices as keys when no headers
          for (let j = 0; j < values.length; j++) {
            let value = options.trimValues ? values[j].trim() : values[j];
            
            // Detect types if enabled
            if (options.detectTypes) {
              value = detectType(value);
            }
            
            obj[`field${j}`] = value;
          }
        }
        
        // Add the JSON object as a line in the output
        jsonlOutput += JSON.stringify(obj) + '\n';
      }
    }
    
    return jsonlOutput;
  } catch (error) {
    throw error;
  }
};

// Helper function to detect and convert types
const detectType = (value: string): any => {
  // Check if the value is empty
  if (value === '') {
    return null;
  }
  
  // Check if the value is null or undefined
  if (value.toLowerCase() === 'null' || value.toLowerCase() === 'undefined') {
    return null;
  }
  
  // Check if the value is a boolean
  if (value.toLowerCase() === 'true') {
    return true;
  }
  if (value.toLowerCase() === 'false') {
    return false;
  }
  
  // Check if the value is a number
  if (/^-?\d+$/.test(value)) {
    return parseInt(value, 10);
  }
  if (/^-?\d+\.\d+$/.test(value)) {
    return parseFloat(value);
  }
  
  // Check if the value is a date (ISO format)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/.test(value)) {
    return value; // Keep as string for consistent JSON serialization
  }
  
  // Otherwise, keep it as a string
  return value;
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// Main component to convert CSV to JSONL
const CsvToJsonl: React.FC<CsvToJsonlProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for the JSONL output
  const [jsonlOutput, setJsonlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isJsonlGenerated, setIsJsonlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for preview
  const [showPreview, setShowPreview] = useState<boolean>(false);
  
  // Conversion options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [detectTypes, setDetectTypes] = useState<boolean>(true);
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsJsonlGenerated(false);
    setShowPreview(false);
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Update the file name based on the uploaded file
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop(); // Remove extension
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const csv = event.target.result as string;
        setCsvInput(csv);
        setError('');
        setIsJsonlGenerated(false);
        setShowPreview(false);
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Product,Category,Price,Stock,Rating
Basic T-Shirt,Clothing,19.99,120,4.2
Premium Hoodie,Clothing,49.99,65,4.8
Wireless Earbuds,Electronics,89.99,42,4.5
Smart Watch,Electronics,199.99,30,4.7
Running Shoes,Footwear,79.99,80,4.3
Yoga Mat,Fitness,29.99,55,4.6
Protein Powder,Nutrition,39.99,100,4.4
Water Bottle,Accessories,14.99,150,4.1`;

    setCsvInput(exampleCsv);
    setError('');
    setIsJsonlGenerated(false);
    setShowPreview(false);
    setJsonlOutput('');
  };

  // Convert CSV to JSONL
  const convertCsvToJsonl = () => {
    setError('');
    setIsLoading(true);
    setIsJsonlGenerated(false);
    setJsonlOutput('');
    setShowPreview(false);
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the JSONL output
      const options = {
        header: hasHeader,
        delimiter,
        trimValues,
        skipEmptyLines,
        detectTypes
      };
      
      const jsonl = csvToJsonl(csvInput, options);
      setJsonlOutput(jsonl);
      setIsJsonlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to JSONL: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setJsonlOutput('');
    setError('');
    setFileName('data');
    setIsJsonlGenerated(false);
    setShowPreview(false);
  };

  // Copy JSONL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download JSONL file
  const downloadJsonl = () => {
    const blob = new Blob([jsonlOutput], { type: 'application/x-jsonlines;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'data'}.jsonl`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Toggle preview
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Format single line for preview
  const formatJsonForPreview = (jsonStr: string): string => {
    try {
      return JSON.stringify(JSON.parse(jsonStr), null, 2);
    } catch (e) {
      return jsonStr;
    }
  };

  // Format the entire JSONL for preview
  const getFormattedPreview = (): React.ReactNode => {
    if (!jsonlOutput.trim()) return null;
    
    const lines = jsonlOutput.trim().split('\n');
    const previewLines = lines.slice(0, 10); // Show first 10 lines
    
    return (
      <div>
        {previewLines.map((line, index) => (
          <div key={index} className="mb-4">
            <div className="text-xs text-gray-500 mb-1">Line {index + 1}:</div>
            <pre className="bg-gray-50 p-3 rounded-md text-sm overflow-x-auto">
              {formatJsonForPreview(line)}
            </pre>
          </div>
        ))}
        {lines.length > 10 && (
          <div className="text-sm text-gray-500 mt-2">
            ...and {lines.length - 10} more lines
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to JSON Lines (JSONL) Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert CSV (Comma-Separated Values) data to JSON Lines (JSONL) format. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Product,Category,Price,Stock
T-Shirt,Clothing,19.99,120
Headphones,Electronics,89.99,42"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Output File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                    First row contains headers
                  </label>
                </div>
                
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="detectTypes"
                    checked={detectTypes}
                    onChange={(e) => setDetectTypes(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="detectTypes" className="ml-2 block text-sm text-gray-700">
                    Detect and convert value types (numbers, booleans, etc.)
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertCsvToJsonl}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to JSONL'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              JSONL Output
            </label>
            
            {isJsonlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy JSONL'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadJsonl}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={togglePreview}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isJsonlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    JSONL output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isJsonlGenerated && !showPreview && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="jsonlOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={jsonlOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
            
            {!error && isJsonlGenerated && showPreview && (
              <div className="flex-grow flex flex-col border border-gray-300 rounded-md p-3 bg-white overflow-auto">
                <div className="prose prose-sm max-w-none">
                  {getFormattedPreview()}
                </div>
              </div>
            )}
          </div>
          
          {isJsonlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">CSV Successfully Converted to JSONL!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated JSONL file for use with systems that process data line by line, such as data pipelines, streaming analytics, and machine learning workflows.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to JSONL Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">What is JSONL (JSON Lines)?</h3>
            <p className="text-gray-600">
              JSON Lines (JSONL) is a format where each line is a valid JSON object, separated by newlines. It's designed for processing data line-by-line, making it ideal for streaming, logging, and data processing pipelines. Unlike regular JSON arrays, JSONL allows for easy appending of new records and processing of extremely large datasets without loading the entire file into memory.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool parses CSV (Comma-Separated Values) data and converts each row into a JSON object, then formats these objects in JSONL format (one JSON object per line). When headers are present, they become the keys in each JSON object. The converter can also intelligently detect and convert data types, making it especially useful for data analysis and machine learning workflows.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
              <li><strong>JSON Key Generation</strong> - Use headers as JSON keys or generate field names</li>
              <li><strong>Type Detection</strong> - Automatically detect and convert numbers, booleans, null values</li>
              <li><strong>Quote Handling</strong> - Properly handle quoted values in CSV files</li>
              <li><strong>Whitespace Trimming</strong> - Option to trim whitespace from field values</li>
              <li><strong>Empty Line Skipping</strong> - Option to ignore empty lines in the input</li>
              <li><strong>Live Preview</strong> - See how your data will look in JSONL format</li>
              <li><strong>JSONL Download</strong> - Save your data as a JSONL file</li>
              <li><strong>Formatted Preview</strong> - View the JSONL data in a readable, indented format</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">JSONL Format Syntax</h3>
            <p className="text-gray-600">
              JSONL (JSON Lines) format consists of valid JSON objects, each on a single line:
            </p>
            <p className="text-gray-600 mt-2">
              This format makes it easy to process data line-by-line, unlike traditional JSON arrays which need to be loaded entirely into memory.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Common Use Cases</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Data ingestion for machine learning pipelines</li>
              <li>Log files and event streaming</li>
              <li>Big data processing (Spark, Hadoop)</li>
              <li>Sequential data analysis</li>
              <li>Incremental data loading</li>
              <li>API response formatting</li>
              <li>Distributed systems data exchange</li>
              <li>NoSQL database imports</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Format Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent delimiter throughout the file</li>
              <li>Include a header row for better object structure</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Escape double quotes within quoted fields by doubling them (e.g., "He said ""Hello"" to me")</li>
              <li>Keep column count consistent across all rows</li>
              <li>Use header names that will make valid JSON keys</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">JSONL Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Ensure each line is a complete, valid JSON object</li>
              <li>Avoid newlines within JSON objects (use escaped \n if needed)</li>
              <li>Use UTF-8 encoding for the file</li>
              <li>Consider compressing large JSONL files (.jsonl.gz) for storage efficiency</li>
              <li>For streaming applications, flush after each line is written</li>
              <li>Validate all objects have consistent schema if needed for your application</li>
              <li>Consider adding a schema definition at the beginning for self-describing files</li>
            </ul>
          </div>

          <div>
            <h3 className="font-medium text-gray-800 mb-2">Working with JSONL files</h3>
            <p className="text-gray-600">
              Many data processing tools can work directly with JSONL files. Some examples include:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Python</strong>: Use the built-in `json` module and process line by line</li>
              <li><strong>Node.js</strong>: Use libraries like `line-by-line` or `readline`</li>
              <li><strong>Pandas</strong>: Use `pd.read_json(filename, lines=True)` to load JSONL directly</li>
              <li><strong>Spark</strong>: Use `spark.read.json(filename)` which natively supports JSONL</li>
              <li><strong>jq</strong>: Use `jq -c` to process JSONL files in the command line</li>
              <li><strong>MongoDB</strong>: Use `mongoimport --jsonArray` with JSONL files for import</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvToJsonl;